import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { css } from '@emotion/react';
import { Box, useMediaQuery, useTheme } from '@mui/material';

import ColorInputPickerV2 from '~/components/builder/ColorInputPickerV2';
import MultiColorPickerV2 from '~/components/builder/MultiColorPickerV2';
import Accordion from '~/components/ui/Accordion/Accordion';
import Select from '~/components/ui/Select';
import { DESKTOP_MIN_WIDTH, FONTS } from '~/constants/common';
import { COUPON_QR_ID_PARAM_NAME } from '~/constants/qr-code';
import { VIDEO_TEMPLATE_COLORS } from '~/constants/video';
import useVideoQrData from '~/hooks/useVideoQrData';
import CreateQRContentBlockLayout from '~/pages/CreateQR/components/CreateQRContentBlockLayout/CreateQRContentBlockLayout';
import { COLORS } from '~/styles/colors';

export default function VideoTemplateAppearance() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { control } = useFormContext();
  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_MIN_WIDTH));
  const [searchParams] = useSearchParams();

  const videoId = searchParams.get(COUPON_QR_ID_PARAM_NAME);

  const styles = useStyles();
  const { data: videoQrData } = useVideoQrData({ videoId });

  const defaultSelectedIndex = VIDEO_TEMPLATE_COLORS.findIndex((color) => color === videoQrData?.design?.primary);

  const initialColors =
    videoQrData && defaultSelectedIndex === -1
      ? [videoQrData.design.primary, ...VIDEO_TEMPLATE_COLORS.slice(1)]
      : VIDEO_TEMPLATE_COLORS;

  return (
    <CreateQRContentBlockLayout type="appearance">
      <Accordion
        defaultExpanded
        variant="child"
        summary={t('basicTemplateLabels:tmplDesign')}
        details={
          <MultiColorPickerV2
            initialColors={initialColors}
            names={['appearance.design.primary']}
            defaultSelectedIndex={defaultSelectedIndex === -1 ? undefined : defaultSelectedIndex}
          />
        }
      />
      <Accordion
        defaultExpanded
        variant="child"
        hideBottomBorder
        summary={t('basicTemplateLabels:tmplFonts')}
        details={
          <>
            <Box flexDirection={isDesktop ? 'row' : 'column'} sx={styles.titleAndTextFontsContainer}>
              <Controller
                control={control}
                name="appearance.fonts.title"
                render={({ field: { value, onChange, onBlur } }) => {
                  return (
                    <Select
                      items={FONTS}
                      onChange={onChange}
                      onBlur={onBlur}
                      fullWidth
                      label={t('other:title')}
                      defaultValue={value}
                    />
                  );
                }}
              />
              <Controller
                control={control}
                name="appearance.fonts.text"
                render={({ field: { value, onChange, onBlur } }) => {
                  return (
                    <Select
                      items={FONTS}
                      onChange={onChange}
                      onBlur={onBlur}
                      fullWidth
                      label={t('other:text')}
                      defaultValue={value}
                    />
                  );
                }}
              />
            </Box>
            <Box flexDirection={isDesktop ? 'row' : 'column'} sx={styles.titleAndTextColorsContainer}>
              <ColorInputPickerV2 name="appearance.fonts.titleColor" label={t('other:titleColor')} fullWidth />
              <ColorInputPickerV2 name="appearance.fonts.textsColor" label={t('other:textsColor')} fullWidth />
            </Box>
          </>
        }
      />
    </CreateQRContentBlockLayout>
  );
}

function useStyles() {
  return {
    titleAndTextFontsContainer: css`
      display: flex;
      gap: 16px;
    `,
    titleAndTextColorsContainer: css`
      display: flex;
      gap: 16px;
      border-radius: 20px;
      padding: 16px;
      background-color: ${COLORS.lightGray};
      margin-top: 16px;
    `,
  };
}
